import React from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { useTranslation } from '../../i18n';
import { UploadButton } from './FileUploader.styled';

interface AddOptionDialogsProps {
  // Customer dialog
  showAddCustomerDialog: boolean;
  setShowAddCustomerDialog: React.Dispatch<React.SetStateAction<boolean>>;
  newCustomerName: string;
  setNewCustomerName: React.Dispatch<React.SetStateAction<string>>;
  addingCustomer: boolean;
  onAddCustomer: () => void;
  // Project dialog
  showAddProjectDialog: boolean;
  setShowAddProjectDialog: React.Dispatch<React.SetStateAction<boolean>>;
  newProjectName: string;
  setNewProjectName: React.Dispatch<React.SetStateAction<string>>;
  addingProject: boolean;
  onAddProject: () => void;
  // Payload dialog
  showAddPayloadDialog: boolean;
  setShowAddPayloadDialog: React.Dispatch<React.SetStateAction<boolean>>;
  newPayloadName: string;
  setNewPayloadName: React.Dispatch<React.SetStateAction<string>>;
  newPayloadWeight: string;
  setNewPayloadWeight: React.Dispatch<React.SetStateAction<string>>;
  addingPayload: boolean;
  onAddPayload: () => void;
}

const AddOptionDialogs: React.FC<AddOptionDialogsProps> = ({
  showAddCustomerDialog,
  setShowAddCustomerDialog,
  newCustomerName,
  setNewCustomerName,
  addingCustomer,
  onAddCustomer,
  showAddProjectDialog,
  setShowAddProjectDialog,
  newProjectName,
  setNewProjectName,
  addingProject,
  onAddProject,
  showAddPayloadDialog,
  setShowAddPayloadDialog,
  newPayloadName,
  setNewPayloadName,
  newPayloadWeight,
  setNewPayloadWeight,
  addingPayload,
  onAddPayload,
}) => {
  const { t } = useTranslation();

  return (
    <>
      {/* Add Customer Dialog */}
      <Dialog
        open={showAddCustomerDialog}
        onClose={() => setShowAddCustomerDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('fileManagement.addNewCustomer')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('fileManagement.customerName')}
            fullWidth
            value={newCustomerName}
            onChange={(e) => setNewCustomerName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newCustomerName.trim()) {
                onAddCustomer();
              }
            }}
            disabled={addingCustomer}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddCustomerDialog(false)} disabled={addingCustomer}>
            {t('common.cancel')}
          </Button>
          <UploadButton
            onClick={onAddCustomer}
            disabled={!newCustomerName.trim() || addingCustomer}
          >
            {addingCustomer ? t('common.adding') : t('common.add')}
          </UploadButton>
        </DialogActions>
      </Dialog>

      {/* Add Project Dialog */}
      <Dialog
        open={showAddProjectDialog}
        onClose={() => setShowAddProjectDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('fileManagement.addNewProject')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('fileManagement.projectName')}
            fullWidth
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newProjectName.trim()) {
                onAddProject();
              }
            }}
            disabled={addingProject}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddProjectDialog(false)} disabled={addingProject}>
            {t('common.cancel')}
          </Button>
          <UploadButton onClick={onAddProject} disabled={!newProjectName.trim() || addingProject}>
            {addingProject ? t('common.adding') : t('common.add')}
          </UploadButton>
        </DialogActions>
      </Dialog>

      {/* Add Payload Dialog */}
      <Dialog
        open={showAddPayloadDialog}
        onClose={() => setShowAddPayloadDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('fileManagement.addNewPayload')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('fileManagement.payloadName')}
            fullWidth
            value={newPayloadName}
            onChange={(e) => setNewPayloadName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newPayloadName.trim()) {
                onAddPayload();
              }
            }}
            disabled={addingPayload}
          />
          <TextField
            margin="dense"
            label={t('fileManagement.payloadWeight')}
            fullWidth
            type="number"
            value={newPayloadWeight}
            onChange={(e) => setNewPayloadWeight(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newPayloadName.trim()) {
                onAddPayload();
              }
            }}
            disabled={addingPayload}
            inputProps={{ step: 0.1, min: 0 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddPayloadDialog(false)} disabled={addingPayload}>
            {t('common.cancel')}
          </Button>
          <UploadButton onClick={onAddPayload} disabled={!newPayloadName.trim() || addingPayload}>
            {addingPayload ? t('common.adding') : t('common.add')}
          </UploadButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddOptionDialogs;
